/**
 * Voice-over hook — plays pre-generated Polly narration audio with auto-advance.
 *
 * Only plays pre-generated audio (audioBase64/audioUrl). If a page has no audio,
 * it auto-advances silently (old stories without narration behave as before).
 *
 * Works on both web (HTMLAudioElement) and native (FTUEAudioPlayerModule).
 */

import { useEffect, useRef, useCallback, useState } from "react";
import { createNarrationPlayer, type NarrationPlayerHandle } from "./NarrationPlayer";

interface VoiceOverPage {
  narrativeText?: string;
  isCover?: boolean;
  audioBase64?: string;
}

interface UseVoiceOverOptions {
  pages: VoiceOverPage[];
  currentPage: number;
  onPageChange: (page: number) => void;
  enabled?: boolean;
  title?: string;
}

export function useVoiceOver({
  pages,
  currentPage,
  onPageChange,
  enabled = true,
}: UseVoiceOverOptions) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const stoppedByUserRef = useRef(false);
  const wasSpeakingOnMuteRef = useRef(false);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playerRef = useRef<NarrationPlayerHandle>(null!);
  if (!playerRef.current) playerRef.current = createNarrationPlayer();
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const clearTimers = useCallback(() => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    stoppedByUserRef.current = true;
    setIsSpeaking(false);
    setIsPaused(false);
    clearTimers();
    playerRef.current.stop();
  }, [clearTimers]);

  const speakPageRef = useRef<(pageIndex: number) => void>(() => {});

  const togglePause = useCallback(() => {
    if (!enabled) return;
    if (isPaused) {
      setIsPaused(false);
      stoppedByUserRef.current = false;
      playerRef.current.resume();
      setIsSpeaking(true);
    } else {
      setIsPaused(true);
      stoppedByUserRef.current = true;
      setIsSpeaking(false);
      clearTimers();
      playerRef.current.pause();
    }
  }, [enabled, isPaused, clearTimers]);

  const advanceToNext = useCallback((pageIndex: number) => {
    if (stoppedByUserRef.current) return;
    if (pageIndex < pagesRef.current.length - 1) {
      advanceTimerRef.current = setTimeout(() => {
        // Only advance if we're still on the same page (user hasn't swiped away)
        if (!stoppedByUserRef.current && currentPageRef.current === pageIndex) {
          onPageChangeRef.current(pageIndex + 1);
        }
      }, 1500);
    }
  }, []);

  const playAudio = useCallback((audioSrc: string, pageIndex: number) => {
    setIsSpeaking(true);
    playerRef.current.play(audioSrc)
      .then(() => {
        setIsSpeaking(false);
        if (!stoppedByUserRef.current) {
          advanceToNext(pageIndex);
        }
      })
      .catch(() => {
        setIsSpeaking(false);
      });
  }, [advanceToNext]);

  const speakPage = useCallback(
    (pageIndex: number) => {
      if (!enabled || pageIndex < 0 || pageIndex >= pagesRef.current.length) return;

      clearTimers();
      stoppedByUserRef.current = false;
      const page = pagesRef.current[pageIndex];

      const startPlayback = () => {
        if (stoppedByUserRef.current || currentPageRef.current !== pageIndex) return;
        if (page.audioBase64) {
          playAudio(`data:audio/mp3;base64,${page.audioBase64}`, pageIndex);
        }
        // No audio — do nothing (user swipes manually)
      };

      advanceTimerRef.current = setTimeout(startPlayback, 1000);
    },
    [enabled, playAudio, advanceToNext, clearTimers],
  );

  speakPageRef.current = speakPage;

  // Re-trigger when Polly audio arrives for the current page
  const currentPageAudio = pages[currentPage]?.audioBase64;

  const wasEnabledRef = useRef(enabled);

  // Handle mute/unmute toggle
  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;

    if (!enabled && wasEnabled) {
      // Muted — pause audio (keep position for resume)
      wasSpeakingOnMuteRef.current = isSpeaking;
      playerRef.current.pause();
      clearTimers();
      stoppedByUserRef.current = true;
      setIsSpeaking(false);
      setIsPaused(true);
    } else if (enabled && !wasEnabled) {
      // Unmuted — resume if audio was mid-playback, otherwise re-read current page
      stoppedByUserRef.current = false;
      setIsPaused(false);
      if (wasSpeakingOnMuteRef.current) {
        wasSpeakingOnMuteRef.current = false;
        playerRef.current.resume();
        setIsSpeaking(true);
      } else {
        speakPage(currentPage);
      }
    }
  }, [enabled, isPaused, clearTimers, speakPage, currentPage]);

  // Speak when page changes or audio becomes available (only if enabled)
  const prevPageRef = useRef(currentPage);
  useEffect(() => {
    const pageChanged = prevPageRef.current !== currentPage;
    prevPageRef.current = currentPage;

    // On page change, always reset pause state (even if muted) — prevents
    // stale audio from resuming when user unmutes on a different page
    if (pageChanged) {
      playerRef.current.stop();
      clearTimers();
      setIsPaused(false);
      wasSpeakingOnMuteRef.current = false;
    }

    if (!enabled) return;

    // If only audio data arrived (not a page change), respect user's explicit pause
    if (!pageChanged && stoppedByUserRef.current) return;

    stoppedByUserRef.current = false;
    speakPage(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentPageAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { stop(); };
  }, [stop]);

  return { isSpeaking, isPaused, stop, togglePause };
}
